import { Star, BadgeCheck, MapPin } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { REVIEW_COUNT_TEXT } from "@/lib/booking";
import {
  reviews as allReviews,
  reviewsForSalon,
  reviewsForSalonByService,
  sortReviews,
  featuredReviews,
  type Review,
} from "@/data/reviews";
import { getSalonReviews, type DbReview } from "@/lib/reviews.functions";
import { salonBySlug, salons } from "@/data/salons";
import { EmptyReviewsState, InvalidReviewsState } from "@/components/EmptyReviewsState";
import type { SalonSlug } from "@/data/types";
import {
  bokadirektCountFor,
  bokadirektRatingFor,
  ratingFor,
  reviewCountFor,
} from "@/data/reviewStats";

interface Props {
  salon?: SalonSlug;
  heading?: string;
  limit?: number;
  service?: string;
}

function formatDate(d: string) {
  try {
    return new Date(d).toLocaleDateString("sv-SE", { year: "numeric", month: "short" });
  } catch {
    return d;
  }
}

/** Ensure variety across salons (one per salon first). */
function diversifyBySalon(list: Review[], limit: number): Review[] {
  const seen = new Set<string>();
  const first: Review[] = [];
  const rest: Review[] = [];
  for (const r of list) {
    if (!seen.has(r.salonSlug)) {
      seen.add(r.salonSlug);
      first.push(r);
    } else {
      rest.push(r);
    }
  }
  return [...first, ...rest].slice(0, limit);
}

/**
 * Interleave Google and BokaDirekt so both sources are always represented.
 * Without this, BokaDirekt's more recent reviews crowd Google out entirely.
 */
function interleaveSources(list: Review[], limit: number): Review[] {
  const google = list.filter((r) => r.source === "Google");
  const boka = list.filter((r) => r.source !== "Google");
  if (google.length === 0 || boka.length === 0) return list.slice(0, limit);
  const out: Review[] = [];
  let gi = 0;
  let bi = 0;
  while (out.length < limit && (gi < google.length || bi < boka.length)) {
    if (bi < boka.length) out.push(boka[bi++]);
    if (out.length < limit && gi < google.length) out.push(google[gi++]);
  }
  return out.slice(0, limit);
}


function dbToReview(r: DbReview): Review {
  const salon = _salonMap[r.salonSlug];
  return {
    id: `db-${r.id}`,
    author: r.author ?? "Anonym kund",
    location: salon?.shortName ?? "Stockholm",
    rating: r.rating,
    text: r.text ?? "",
    salonSlug: r.salonSlug,
    date: r.reviewDate,
    source: r.source === "google" ? "Google" : "BokaDirekt",
    verified: true,
  };
}

const _salonMap = salonBySlug;

export function ReviewsBlock({ salon, heading, limit = 6, service }: Props) {
  const fetchReviews = useServerFn(getSalonReviews);
  const { data: dbRows } = useQuery({
    queryKey: ["salon-reviews", salon ?? "all", 50, 5],
    queryFn: () => fetchReviews({ data: { salonSlug: salon, limit: 50, minRating: 5 } }),
    staleTime: 1000 * 60 * 30, // 30 min
  });

  let list: Review[] = [];
  let error: unknown = null;
  try {
    const dbReviews = (dbRows ?? []).map(dbToReview).filter((r) => r.text);
    if (dbReviews.length > 0) {
      const target = salon ? limit : Math.max(limit, 3);
      const bySalon = salon ? dbReviews : diversifyBySalon(dbReviews, dbReviews.length);
      list = interleaveSources(bySalon, target);
    } else if (salon) {
      list = (service ? reviewsForSalonByService(salon, service) : reviewsForSalon(salon)).slice(0, limit);
    } else {
      const pool = sortReviews(
        Array.from(new Map([...featuredReviews, ...allReviews].map((r) => [r.id, r])).values()),
      );
      list = diversifyBySalon(pool, Math.max(limit, 3));
    }
  } catch (e) {
    error = e;
  }

  const showInvalid = error && import.meta.env.DEV;
  const isEmpty = !error && list.length === 0;
  const contextSalon = salon ? salonBySlug[salon] : undefined;

  // Metadata for freshness/source display
  const dbReviewsAll = (dbRows ?? []).map(dbToReview);
  const sourcesInView = new Set(list.map((r) => r.source));
  const latestBySource = (dbRows ?? []).reduce<{ google: string | null; bokadirekt: string | null }>(
    (acc, r) => {
      if (!r.importedAt) return acc;
      const key = r.source;
      if (!acc[key] || r.importedAt > acc[key]!) acc[key] = r.importedAt;
      return acc;
    },
    { google: null, bokadirekt: null },
  );
  const hasLiveData = dbReviewsAll.length > 0;

  const formatUpdated = (iso: string) => {
    try {
      const d = new Date(iso);
      const diffMs = Date.now() - d.getTime();
      const diffH = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffH < 1) return "för mindre än en timme sedan";
      if (diffH < 24) return `för ${diffH} ${diffH === 1 ? "timme" : "timmar"} sedan`;
      const diffD = Math.floor(diffH / 24);
      if (diffD < 7) return `för ${diffD} ${diffD === 1 ? "dag" : "dagar"} sedan`;
      return d.toLocaleDateString("sv-SE", { year: "numeric", month: "long", day: "numeric" });
    } catch {
      return iso;
    }
  };

  // Public totals come from the central, source-specific review registry.
  const salonAggregates = salon
    ? salons.filter((s) => s.slug === salon)
    : salons;
  const googleCount = salonAggregates.reduce((sum, s) => sum + reviewCountFor(s.slug), 0);
  const googleAvg =
    googleCount > 0
      ? (
          salonAggregates.reduce(
            (sum, s) => sum + ratingFor(s.slug) * reviewCountFor(s.slug),
            0,
          ) / googleCount
        )
          .toFixed(1)
          .replace(".", ",")
      : null;
  const bokadirektCount = salonAggregates.reduce((sum, s) => sum + bokadirektCountFor(s.slug), 0);
  const bokadirektAvg = bokadirektCount > 0
    ? (salonAggregates.reduce(
        (sum, s) => sum + bokadirektRatingFor(s.slug) * bokadirektCountFor(s.slug),
        0,
      ) / bokadirektCount).toFixed(1).replace(".", ",")
    : null;

  const sourceRows: Array<{
    key: "google" | "bokadirekt";
    label: string;
    chipClass: string;
    updatedAt: string | null;
    count: number;
    avg: string | null;
    show: boolean;
  }> = [
    {
      key: "google",
      label: "Google",
      chipClass: "bg-foreground/5 text-foreground/75",
      updatedAt: latestBySource.google,
      count: googleCount,
      avg: googleAvg,
      show: sourcesInView.has("Google") || googleCount > 0,
    },
    {
      key: "bokadirekt",
      label: "BokaDirekt",
      chipClass: "bg-brass/15 text-brass-deep",
      updatedAt: latestBySource.bokadirekt,
      count: bokadirektCount,
      avg: bokadirektAvg,
      show: true,
    },
  ];

  return (
    <section className="container-page py-16 md:py-24" aria-labelledby="reviews-heading">
      <div className="max-w-2xl">
        <div className="eyebrow">Omdömen</div>
        <h2 id="reviews-heading" className="mt-3 text-3xl md:text-4xl">
          {heading ?? `${REVIEW_COUNT_TEXT} från Google och BokaDirekt`}
        </h2>
        <p className="mt-3 text-muted-foreground text-sm">
          Vi samlar äkta omdömen från både <strong className="text-foreground">Google</strong> och{" "}
          <strong className="text-foreground">BokaDirekt</strong>, uppdaterat löpande från våra fyra
          salonger i Stockholm.
        </p>

        <dl className="mt-4 flex flex-col gap-2 text-xs text-muted-foreground sm:flex-row sm:flex-wrap sm:gap-x-6 sm:gap-y-2">
          {sourceRows
            .filter((row) => row.show)
            .map((row) => (
              <div key={row.key} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                {row.avg && row.count > 0 ? (
                  <dd className="inline-flex items-center gap-1 text-foreground/85">
                    <Star size={12} className="fill-brass text-brass" aria-hidden="true" />
                    <span className="font-semibold">
                      {row.avg} av 5 på {row.label} ({row.count.toLocaleString("sv-SE")} omdömen)
                    </span>
                  </dd>
                ) : (
                  <dd className="italic">Inga recensioner ännu</dd>
                )}
                <dd className="inline-flex basis-full items-center gap-1.5 pl-1">
                  {row.updatedAt ? (
                    <>
                      <span
                        className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                        aria-hidden="true"
                      />
                      <span>
                        <span className="font-medium text-foreground/80">Senast uppdaterad:</span>{" "}
                        <time dateTime={row.updatedAt}>{formatUpdated(row.updatedAt)}</time>
                      </span>
                    </>
                  ) : (
                    <span className="italic">
                      {hasLiveData ? "Ingen live-synk ännu" : "Live-synk aktiveras vid nästa körning"}
                    </span>
                  )}
                </dd>
              </div>
            ))}
        </dl>
      </div>

      {showInvalid ? (
        <div className="mt-10">
          <InvalidReviewsState error={error} />
        </div>
      ) : isEmpty || error ? (
        <div className="mt-10">
          <EmptyReviewsState salon={contextSalon} />
        </div>
      ) : (
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {list.map((r) => {
            const reviewSalon = salonBySlug[r.salonSlug];
            const salonName = reviewSalon?.shortName ?? "";
            return (
              <figure key={r.id} className="card-elev p-6 flex flex-col gap-3 relative">
                <div className="flex items-center justify-between gap-3">
                  <div
                    className="flex items-center gap-0.5 text-brass"
                    role="img"
                    aria-label={`${r.rating} av 5 stjärnor`}
                  >
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        size={16}
                        className={i < r.rating ? "fill-brass" : "opacity-25"}
                      />
                    ))}
                  </div>
                  <span
                    className={`shrink-0 text-[10px] font-medium tracking-[0.12em] uppercase px-2 py-1 rounded ${
                      r.source === "Google"
                        ? "bg-foreground/5 text-foreground/70"
                        : "bg-brass/15 text-brass-deep"
                    }`}
                    aria-label={`Källa: ${r.source}`}
                  >
                    {r.source}
                  </span>
                </div>

                <blockquote className="text-sm leading-relaxed text-foreground/85">
                  &ldquo;{r.text}&rdquo;
                </blockquote>

                <figcaption className="mt-auto pt-3 border-t border-border text-xs text-muted-foreground flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1 font-medium text-foreground">
                      <span className="truncate">{r.author}</span>
                      {r.verified && (
                        <BadgeCheck
                          size={14}
                          className="shrink-0 text-brass"
                          aria-label="Verifierad recension"
                        />
                      )}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 truncate">
                      <MapPin size={11} className="shrink-0 opacity-70" />
                      <span className="truncate">{r.location}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {salonName && <div className="text-foreground/80">{salonName}</div>}
                    <div>{formatDate(r.date)}</div>
                  </div>
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}
    </section>
  );
}
